import { Skeleton } from '@mantine/core';
import { formatTonnage } from '../../utils/formatting.js';
import classes from './SummaryBar.module.css';

/**
 * `highlight` (Standard): gelber Gesamtsummen-Balken -- Flaeche #FFF3BF mit Rahmen
 * #FFE066, Schrift und Zahlen-Chip in #E67700, Zahl weiss. `muted`: die
 * zurueckhaltende Zwischensumme -- Flaeche #F5F5F5 mit Rahmen #D7D7D7, Schrift
 * #4A4A4A, Chip #8F8B89, Zahl weiss.
 *
 * Beide Varianten teilen sich Typografie (700 / 26px / 135 %) und Layout; eine Variante
 * legt nur ihre vier Farben fest (siehe SummaryBar.module.css). Neue Einsatzorte sehen
 * dadurch zwangslaeufig identisch aus.
 */
export type SummaryBarVariant = 'highlight' | 'muted';

/**
 * Abgleich gegen einen Wert aus einem anderen Formular. `value` `null` bedeutet „der
 * Referenzwert liegt noch nicht vor"; dann entfaellt auch die Differenz.
 */
export interface SummaryBarReconciliation {
  label: string;
  value: string | null;
  /** Vorzeichenbehaftete Differenz als Dezimalstring, `null` wenn nicht berechenbar. */
  difference: string | null;
  /** Text anstelle des Werts, solange `value` fehlt. */
  emptyText?: string;
}

interface SummaryBarProps {
  label: string;
  value: string | null;
  /** Einheit neben dem Wert im Chip, Standard `'t'`. */
  unit?: string;
  variant?: SummaryBarVariant;
  /**
   * Kontrollzeile unter dem Balken (Versand nach Produktkategorien gegen die Jahressumme
   * Inlandsversand, D-31). Bewusst
   * ohne eigene Flaeche, damit der Summenbalken der einzige farbige Block bleibt.
   */
  reconciliation?: SummaryBarReconciliation;
  loading?: boolean;
}

/**
 * Geteilter Summenbalken (D-08): loest die frueher je Formular eigenstaendig gebaute
 * zweite Karte mit "nackter Zahl" ab. Genutzt von Inlandsversand und Versand nach Produktkategorien (D-31, Plan 23-04)
 * sowie Formular D (Plan 23-06).
 *
 * Bewusst ohne Mantine-`Text`/`Card`: deren Basisklassen setzen `font-size`/`line-height`
 * mit gleicher Spezifitaet und haben die Vorgaben aus dem CSS-Modul ueberschrieben.
 * Ausdruecklich NICHT enthalten (D-08 entfernt beides): kein Rechner-Icon, keine
 * Doppelueberschrift aus Kleinversalien-Zeile + `Title`.
 */
export function SummaryBar({
  label,
  value,
  unit = 't',
  variant = 'highlight',
  reconciliation,
  loading,
}: SummaryBarProps) {
  // Eine Abweichung faerbt die Differenz orange. Der Vergleich laeuft numerisch, weil
  // der Rechner "0.00" und "-0.00" liefern kann -- beides ist Uebereinstimmung.
  const hasDeviation =
    reconciliation?.difference != null && Number(reconciliation.difference) !== 0;

  return (
    <div className={classes.wrapper}>
      <div className={classes.root} data-variant={variant}>
        <span className={classes.label}>{label}</span>

        {loading ? (
          <Skeleton width={180} height={43} radius="sm" />
        ) : (
          <div className={classes.chip}>
            <span className={classes.value}>{formatTonnage(value)}</span>
            <span className={classes.value}>{unit}</span>
          </div>
        )}
      </div>

      {reconciliation && (
        <div className={classes.reconciliation}>
          <span className={classes.pair}>
            <span className={classes.pairLabel}>{reconciliation.label}</span>
            <span className={classes.pairValue}>
              {reconciliation.value == null
                ? (reconciliation.emptyText ?? 'noch nicht erfasst')
                : `${formatTonnage(reconciliation.value)} ${unit}`}
            </span>
          </span>

          {reconciliation.difference != null && (
            <span className={classes.pair}>
              <span className={classes.pairLabel}>Differenz</span>
              <span className={classes.pairValue} data-off={hasDeviation}>
                {formatTonnage(reconciliation.difference)} {unit}
              </span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
